import KYCForm from '@company-common/model/kyc-form/KYCForm'
import KYCValidators from './KYCValidators'
import PretransactionalChecklist from '@/views/kyc-pdf/model/ptc/PretransactionalChecklist'
import NeedsBase from '@master/model/kyc-form/need-analysis/NeedsBase'
import { get } from 'lodash/fp'
import { highRiskCheck } from '@master/logic/PretransactionsLogic'
import highRiskCountryList from '@company-common/config/high-risk-country.json'
import ExistingInvestmentV2 from '@master/model/kyc-form/existing-plans/ExistingInvestmentV2'
import CustomExistingPortfolioV2 from '@company-common/model/kyc-form/existing-plans/CustomExistingPortfolioV2'
import { BasicProductV2 as BasicProduct } from '@master/model/kyc-form/product/BasicProductV2'

/**
 * Temporary solutions after updated the JPara @core, @master, @company-common to the latest branch
 * Will need to change the logic from frontend to @core or @master
 */

export default class KYCFormValidation {
  private kycForm!: KYCForm
  private validators!: KYCValidators
  private verifiedPretransactionChecklist!: PretransactionalChecklist

  constructor (kycForm: KYCForm, pretransactionChecklist: PretransactionalChecklist) {
    this.kycForm = kycForm
    this.verifiedPretransactionChecklist = pretransactionChecklist
    this.validators = new KYCValidators()
  }

  validate () {
    return this.validationProgress(this.validators)
  }

  protected validationProgress (validators: KYCValidators) {
    const validationResult = []

    const rpaNotRequired = this.kycForm.client1.rpa.rpaCompleted // this is a shared attribute

    // Validate Client 1 information
    validationResult.push(...validators.personalDetails.validate(this.kycForm.client1.personalDetails, 'Client 1'))
    validationResult.push(...validators.switching.validate(this.kycForm.client1.switching, 'Client 1'))
    if (!rpaNotRequired) validationResult.push(...validators.rpa.validate(this.kycForm.client1.rpa, 'Client 1'))
    validationResult.push(...validators.basis.validate(this.kycForm.client1.recommendationBasis, this.kycForm.recommendations.rcmd, 'Client 1'))
    validationResult.push(...validators.cka.validate(this.kycForm.client1.cka, this.kycForm.client1.cka, 'Client 1'))
    const {
      deathProtectionNeeds,
      disabilityIncomeNeeds,
      criticalIllnessNeeds,
      disabilityProtectionNeeds,
      longTermProtectionClient,
      dependantsProtection,
      personalAccidentNeeds,
      hospitalizationNeeds,
      retirementNeeds,
      savingsNeeds,
      investmentNeeds,
      educationNeeds,
      other1Needs,
      other2Needs
    } = this.kycForm.client1
    const needs = [
      deathProtectionNeeds,
      disabilityIncomeNeeds,
      criticalIllnessNeeds,
      disabilityProtectionNeeds,
      longTermProtectionClient,
      dependantsProtection,
      personalAccidentNeeds,
      hospitalizationNeeds,
      retirementNeeds,
      savingsNeeds,
      investmentNeeds,
      educationNeeds,
      other1Needs,
      other2Needs
    ]
    validationResult.push(...validators.identifyingYourPriorities.validate(needs, 'Client 1'))

    validationResult.push(...validators.aml.validate(this.kycForm.client1.aml, 'Client 1'))
    validationResult.push(...validators.criticalIllness.validate(this.kycForm.client1.criticalIllnessNeeds, 'Client 1'))
    validationResult.push(...validators.deathProtection.validate(this.kycForm.client1.deathProtectionNeeds, 'Client 1'))
    validationResult.push(...validators.disabilityIncomeProtection.validate(this.kycForm.client1.disabilityIncomeNeeds, 'Client 1'))
    validationResult.push(...validators.disabilityProtection.validate(this.kycForm.client1.disabilityProtectionNeeds, 'Client 1'))
    validationResult.push(...validators.longTermNeeds.validate(this.kycForm.client1.longTermProtectionClient, 'Client 1'))
    validationResult.push(...validators.investmentNeeds.validate(this.kycForm.client1.investmentNeeds, 'Client 1'))
    validationResult.push(...validators.retirementNeeds.validate(this.kycForm.client1.retirementNeeds, 'Client 1'))
    validationResult.push(...validators.savingNeeds.validate(this.kycForm.client1.savingsNeeds, 'Client 1'))
    validationResult.push(...validators.otherNeeds.validate(this.kycForm.client1.other1Needs, 'Client 1'))
    validationResult.push(...validators.otherNeeds.validate(this.kycForm.client1.other2Needs, 'Client 1'))
    validationResult.push(...validators.hospitalizationNeeds.validate(this.kycForm.client1.hospitalizationNeeds, 'Client 1'))
    validationResult.push(...validators.personalAccidentNeeds.validate(this.kycForm.client1.personalAccidentNeeds, 'Client 1'))
    validationResult.push(...validators.netWorth.validate(this.kycForm.client1.netWorth, this.kycForm.client1.netWorth.netWorthPlanning, 'Client 1'))
    validationResult.push(...validators.cashFlow.validate(this.kycForm.client1.cashFlow, this.kycForm.client1.cashFlow.cashFlowPlanning, 'Client 1'))
    validationResult.push(...validators.budget.validate(this.kycForm.client1.budget, 'Client 1'))
    validationResult.push(...validators.thirdParty.validate(this.kycForm.client1.thirdPartyPayor, this.kycForm.allRecommendations, 'Client 1'))
    validationResult.push(...validators.beneficialOwner.validate(this.kycForm.client1.beneficialOwner, 'Client 1'))

    validationResult.push(...validators.clientDeclaration.validatePEP(this.kycForm.clientDeclaration.client0DeclarationPoliticallyExposed, 'Client 1'))

    // Validate Client 2 if it is included
    if (this.kycForm.client2.personalDetails.included) {
      // Validate Client 2 information
      validationResult.push(...validators.personalDetails.validate(this.kycForm.client2.personalDetails, 'Client 2'))
      validationResult.push(...validators.switching.validate(this.kycForm.client2.switching, 'Client 2'))
      if (!rpaNotRequired) validationResult.push(...validators.rpa.validate(this.kycForm.client2.rpa, 'Client 2'))
      validationResult.push(...validators.basis.validate(this.kycForm.client2.recommendationBasis, this.kycForm.recommendations.rcmd, 'Client 2'))
      validationResult.push(...validators.cka.validate(this.kycForm.client2.cka, this.kycForm.client1.cka, 'Client 2'))
      const {
        deathProtectionNeeds,
        disabilityIncomeNeeds,
        criticalIllnessNeeds,
        disabilityProtectionNeeds,
        longTermProtectionClient,
        dependantsProtection,
        personalAccidentNeeds,
        hospitalizationNeeds,
        retirementNeeds,
        savingsNeeds,
        investmentNeeds,
        educationNeeds,
        other1Needs,
        other2Needs
      } = this.kycForm.client2
      const needs = [
        deathProtectionNeeds,
        disabilityIncomeNeeds,
        criticalIllnessNeeds,
        disabilityProtectionNeeds,
        longTermProtectionClient,
        dependantsProtection,
        personalAccidentNeeds,
        hospitalizationNeeds,
        retirementNeeds,
        savingsNeeds,
        investmentNeeds,
        educationNeeds,
        other1Needs,
        other2Needs
      ]
      validationResult.push(...validators.identifyingYourPriorities.validate(needs, 'Client 2'))
      validationResult.push(...validators.aml.validate(this.kycForm.client2.aml, 'Client 2'))
      validationResult.push(...validators.criticalIllness.validate(this.kycForm.client2.criticalIllnessNeeds, 'Client 2'))
      validationResult.push(...validators.deathProtection.validate(this.kycForm.client2.deathProtectionNeeds, 'Client 2'))
      validationResult.push(...validators.disabilityIncomeProtection.validate(this.kycForm.client2.disabilityIncomeNeeds, 'Client 2'))
      validationResult.push(...validators.disabilityProtection.validate(this.kycForm.client2.disabilityProtectionNeeds, 'Client 2'))
      validationResult.push(...validators.longTermNeeds.validate(this.kycForm.client2.longTermProtectionClient, 'Client 2'))
      validationResult.push(...validators.investmentNeeds.validate(this.kycForm.client2.investmentNeeds, 'Client 2'))
      validationResult.push(...validators.retirementNeeds.validate(this.kycForm.client2.retirementNeeds, 'Client 2'))
      validationResult.push(...validators.savingNeeds.validate(this.kycForm.client2.savingsNeeds, 'Client 2'))
      validationResult.push(...validators.otherNeeds.validate(this.kycForm.client2.other1Needs, 'Client 2'))
      validationResult.push(...validators.otherNeeds.validate(this.kycForm.client2.other2Needs, 'Client 2'))
      validationResult.push(...validators.hospitalizationNeeds.validate(this.kycForm.client2.hospitalizationNeeds, 'Client 2'))
      validationResult.push(...validators.personalAccidentNeeds.validate(this.kycForm.client2.personalAccidentNeeds, 'Client 2'))
      validationResult.push(...validators.netWorth.validate(this.kycForm.client2.netWorth, this.kycForm.client1.netWorth.netWorthPlanning, 'Client 2'))
      validationResult.push(...validators.cashFlow.validate(this.kycForm.client2.cashFlow, this.kycForm.client1.cashFlow.cashFlowPlanning, 'Client 2'))
      validationResult.push(...validators.budget.validate(this.kycForm.client2.budget, 'Client 2'))
      validationResult.push(...validators.thirdParty.validate(this.kycForm.client2.thirdPartyPayor, this.kycForm.allRecommendations, 'Client 2'))
      validationResult.push(...validators.beneficialOwner.validate(this.kycForm.client2.beneficialOwner, 'Client 2'))

      validationResult.push(...validators.clientDeclaration.validatePEP(this.kycForm.clientDeclaration.client1DeclarationPoliticallyExposed, 'Client 2'))
    }

    // Validate all shared information
    this.kycForm.dependants.forEach((dependant, index) => {
      validationResult.push(...validators.dependant.validate(dependant, index + 1, 'Common'))
    })

    validationResult.push(...validators.dependentsProtection.validate(this.kycForm.dependantsProtectionClient, this.kycForm.client1.dependantsProtection, this.kycForm.client2.dependantsProtection, this.kycForm.hasClient2, 'Common'))

    const educationNeedsForC1C2 = [this.kycForm.client1.educationNeeds, this.kycForm.client2.educationNeeds]
    this.kycForm.educationNeedsDetails.forEach((educationNeedDetail, index) => {
      validationResult.push(...validators.educationNeeds.validate(educationNeedDetail, index, educationNeedsForC1C2, 'Common'))
    })

    validationResult.push(...validators.clientDeclaration.validate(this.kycForm.clientDeclaration, this.kycForm.trustedIndividual, this.kycForm.client2.personalDetails.included, this.kycForm, 'Common'))
    validationResult.push(...validators.trustedIndividual.validate(this.kycForm.trustedIndividual, this.kycForm.client1.personalDetails, this.kycForm.client2.personalDetails, this.kycForm.clientDeclaration, this.kycForm, 'Common'))
    validationResult.push(...validators.sharedRcmdBasis.validate(this.kycForm.client1.recommendationBasis, this.kycForm.client2.recommendationBasis, this.kycForm.clientDeclaration, this.kycForm.recommendations.rcmd, 'Common'))

    validationResult.push(...validators.ilpPortfolio.validate(this.kycForm.newIlpPortfolios.ilpPortfolios, this.kycForm.recommendations.validBasicProduct, 'Common'))

    validationResult.push(...validators.existingPorfolioDeclaration.validate(this.kycForm.existingPortfolio.declaration, this.kycForm.existingPortfolio.portfolios, 'Common'))
    validationResult.push(...validators.existingInvestmentDeclaration.validate(this.kycForm.existingInvestment.declaration, this.kycForm.existingInvestment.portfolios, 'Common'))

    this.kycForm.existingPortfolio.portfolios
      .filter((portfolio: CustomExistingPortfolioV2) => portfolio.owner === 'C1' || (this.kycForm.hasClient2 && portfolio.owner === 'C2'))
      .forEach((portfolio: CustomExistingPortfolioV2, index) => {
        validationResult.push(...validators.existingPortfolio.validate(portfolio, index + 1, this.kycForm.existingPortfolio.declaration, 'Common'))
      })

    this.kycForm.existingInvestment.portfolios
      .filter((portfolio: ExistingInvestmentV2) => portfolio.ownedBy === 'C1' || (this.kycForm.hasClient2 && portfolio.ownedBy === 'C2'))
      .forEach((portfolio: ExistingInvestmentV2, index) => {
        validationResult.push(...validators.existingInvest.validate(portfolio, index + 1, this.kycForm.existingInvestment.declaration, 'Common'))
      })

    validationResult.push(...validators.sharedCka.validate(this.kycForm.client1.cka, 'Common'))
    validationResult.push(...validators.netWorth.validateShare(this.kycForm.client1.netWorth, 'Common'))
    validationResult.push(...validators.cashFlow.validateShare(this.kycForm.client1.cashFlow, 'Common'))
    validationResult.push(...validators.budget.validateShare(this.kycForm.client1.budget, this.kycForm.client2.budget, 'Common'))
    validationResult.push(...validators.adviserDetails.validate(this.kycForm.adviser.details, 'Adviser'))
    validationResult.push(...validators.adviserDeclaration.validate(this.kycForm.adviser.declaration, [this.kycForm.client1.switching, this.kycForm.client2.switching], 'Adviser'))

    // Specify to SonyLife changes
    validationResult.push(...validators.switching.validateShare(this.kycForm.client1.switching, this.kycForm.client2.switching, 'Common'))

    // Product Recommendations check
    const client1Needs: NeedsBase[] = Object.keys(this.kycForm.client1).filter((key) => { return (get(key, this.kycForm.client1) instanceof NeedsBase) }).map((key) => { return get(key, this.kycForm.client1) })
    const client2Needs: NeedsBase[] = Object.keys(this.kycForm.client2).filter((key) => { return (get(key, this.kycForm.client2) instanceof NeedsBase) }).map((key) => { return get(key, this.kycForm.client2) })
    validationResult.push(...validators.recommendations.validate(this.kycForm.recommendations.flatAllProducts as BasicProduct[], client1Needs, client2Needs, this.kycForm.hasClient2, 'Products'))
    this.kycForm.recommendations.flatProductWithInvestMainAccount.forEach((product, index) => {
      validationResult.push(...validators.products.validate(product, index, 'Common'))
    })

    if (highRiskCheck(0, this.kycForm.client1.personalDetails.citizenship, this.kycForm.client1.personalDetails.citizenshipSpecified, this.kycForm.recommendations.rcmd, highRiskCountryList, this.kycForm.client1.personalDetails.citizenshipSpecified2)) {
      validationResult.push(...validators.ecdd.validate(this.kycForm.client1.ecdd, 'Client 1'))
    }

    if (highRiskCheck(1, this.kycForm.client2.personalDetails.citizenship, this.kycForm.client2.personalDetails.citizenshipSpecified, this.kycForm.recommendations.rcmd, highRiskCountryList, this.kycForm.client2.personalDetails.citizenshipSpecified2)) {
      validationResult.push(...validators.ecdd.validate(this.kycForm.client2.ecdd, 'Client 2'))
    }

    // Organise all into sections
    const structured: any = {}
    validationResult.forEach(({ message, sections, category, belongsTo, key, index }) => {
      sections.forEach((section) => {
        // if structured has no section, create one
        if (!structured[section]) {
          structured[section] = {}
        }

        // if current section is new, create one that belongsTo it
        if (!structured[section][belongsTo]) {
          structured[section][belongsTo] = []
        }

        structured[section][belongsTo].push({ message, sections, category, belongsTo, key, index })
      })
    })
    return structured
  }
}
